import {LayerDef} from "../../types";
import {GeoJsonLayer, IconLayer, Layer} from "deck.gl";
import {IconUtil} from "../../util/IconUtil";
import {IconInfo} from "../../util/IconInfo";
import {DatasetRow} from "../../data/Dataset";
import {getSelectionManager} from "../../store/AppState";
import IconClusterLayer from "./IconClusterLayer";
import {PolygonProvider} from "../../util/PolygonProvider";
import {getAppComponent, getMapComponent} from "../../store/Accessor";
import {ColorUtils} from "../../util/ColorUtils";
import {LayerUtil} from "../LayerUtil";


export class DeckMapFactory {

    private createLayerId = (layerDef: LayerDef, suffix: string) => layerDef.id + "____" + suffix;

    public matchesLayerDef = (layer: Layer, layerDef) => layer.id?.startsWith(layerDef?.id + "____");

    private createPointLayer = (
        layerDef: LayerDef,
        reactOnHover: () => boolean,
        setTooltip: (tooltip: JSX.Element, isPinned: boolean) => void
    ): IconLayer => {
        const dataset = layerDef.dataset;
        const [minSize, maxSize] = dataset.getMinMax(layerDef.config.sizeFieldId);
        const buckets = dataset.createBucketsByField(layerDef.config.colorFieldId, layerDef.config.colorPalette.length);
        const dataRows = LayerUtil.prepareDataRows(layerDef, layerDef.dataset.getDataRows(), buckets).filter(row => row.hasValidLatLon());
        const iconDataUrl = IconUtil.iconInfoToURL(IconUtil.getPointShapeById(layerDef.config.shape)?.iconInfo ?? IconInfo.CIRCLE );

        const layerConfig = layerDef.config;

        const icon = {
            url: iconDataUrl,
            width: IconUtil.MAX_ICON_SIZE,
            height: IconUtil.MAX_ICON_SIZE,
            mask: true,
        };

        return new IconLayer({
            id: this.createLayerId(layerDef, "icon"),
            data: dataRows,
            pickable: true,
            getIcon: () => icon,
            getAngle: (dataRow) => {
                if (layerDef.config.useHeadingField && layerDef.headingFieldId) {
                    let angle = (dataRow as DatasetRow).getValue(layerDef.headingFieldId);
                    if (angle) {
                        return -angle;
                    }
                }
                return 0;
            },
            billboard: false,
            getPosition: row => (row as DatasetRow).getLonLat(),
            getSize: row => {
                return layerConfig.sizeDependsOnSizeField && layerConfig.sizeFieldId ?
                    IconUtil.getIconSizeForValue(
                        (row as DatasetRow).getNumberValue(layerConfig.sizeFieldId),
                        minSize,
                        maxSize,
                        Math.max(5, layerConfig.size * 0.33),
                        layerConfig.size
                    ) * 0.5 :
                    layerConfig.size * 0.5;
            },
            getColor: row => LayerUtil.calcFillColor(layerDef, row as DatasetRow, buckets)?.toTriple(),
            opacity: layerDef.config.opacity / 100,
            onHover: info => {
                if (reactOnHover()) {
                    const tt = LayerUtil.createTooltip(info, layerDef, false);
                    setTooltip(tt, false);
                }
            },
            onClick: info => {
                const tt = LayerUtil.createTooltip(info, layerDef, true);
                setTooltip(tt, true);
                // symbol selection on click?
                if (info.object) {
                    let symbol = (info.object as DatasetRow).getIdent();
                    if (symbol) {
                        getSelectionManager().informListeners(symbol);
                    }
                }

            },
            autoHighlight: true,
            highlightColor: [0, 0, 0, 48],
            visible: !layerDef.isHidden
        });
    }

    private createClusterLayer = (
        layerDef: LayerDef,
        reactOnHover: () => boolean,
        setTooltip: (tooltip: JSX.Element, isPinned: boolean) => void
    ): IconClusterLayer => {
        const buckets = layerDef.dataset.createBucketsByField(layerDef.config.colorFieldId, layerDef.config.colorPalette.length);
        const dataRows = LayerUtil.prepareDataRows(layerDef, layerDef.dataset.getDataRows(), buckets).filter(row => row.hasValidLatLon());

        return new IconClusterLayer({
            id: this.createLayerId(layerDef, "cluster"),
            data: dataRows,
            buckets: buckets,
            sizeScale: layerDef.config.size,
            pickable: true,
            opacity: layerDef.config.opacity / 100,
            layerDef: layerDef,
            onHover: info => {
                if (reactOnHover()) {
                    const tt = LayerUtil.createTooltip(info, layerDef, false);
                    setTooltip(tt, false);
                }
            },
            onClick: info => {
                const tt = LayerUtil.createTooltip(info, layerDef, true);
                setTooltip(tt, true);
            },
            visible: !layerDef.isHidden,
        });
    }

    private createGeojsonLayer = (
        layerDef: LayerDef,
        reactOnHover: () => boolean,
        setTooltip: (tooltip: JSX.Element, isPinned: boolean) => void
    ): GeoJsonLayer => {
        const layerConfig = layerDef.config;
        const dataset = layerDef.dataset;

        const polygonProvider = PolygonProvider.getInstance();

        let datasetRowsByGeometries = layerDef.dataset.collectDatasetRowsByGeometry(layerDef.geoFieldType);

        if (datasetRowsByGeometries != null && datasetRowsByGeometries.length) {
            polygonProvider.startJobIfNeeded(
                datasetRowsByGeometries.map(d => [d.country, d.state, d.county]),
                job => {
                    if (job.isDone()) {
                        getMapComponent().updateLayers(layerDef);
                    }
                    getAppComponent().updatePolygonProviderStatus();
                }
            );
        }

        const buckets = dataset.createBucketsByField(layerDef.config.colorFieldId, layerDef.config.colorPalette.length);

        const features = [];
        for (const d of datasetRowsByGeometries) {
            let dataRows = d.dataRows;

            // filter if some colors are hidden
            dataRows = LayerUtil.prepareDataRows(layerDef, dataRows, buckets);
            if (dataRows.length > 0) {
                const g = polygonProvider.getGeometry(d.country, d.state, d.county) as any;
                if (g) {
                    features.push({
                        type: 'Feature',
                        geometry: g,
                        properties: { data: dataRows }
                    });
                }
            }
        }

        // let binaryFeatures = geojsonToBinary(features);
        // const data: any = {
        //     shape: 'binary-feature-collection',
        //     ...binaryFeatures
        // };

        const data: any = {
            type: 'FeatureCollection',
            features: features
        };

        const createTooltip = (d: any, pinned: boolean) => {
            let tt = null;
            if (d.object?.properties) {
                const info: any = {x: d.x, y: d.y};
                const rows = d.object?.properties.data as DatasetRow[];
                if (rows.length > 0) {
                    if (rows.length === 1) {
                        info.object = rows[0];
                    } else {
                        info.objects = rows;
                    }
                    tt = LayerUtil.createTooltip(info, layerDef, pinned);
                }
            }
            setTooltip(tt, pinned);
        }

        return new GeoJsonLayer({
            id: this.createLayerId(layerDef, "geojson"),
            data: data,
            filled: true,
            stroked: false,
            getFillColor: d => LayerUtil.calcFillColor(layerDef, d.properties?.data[0] as DatasetRow, buckets)?.toTriple(),
            opacity: layerConfig.opacity / 100,
            pickable: true,
            onHover: d => {
                if (reactOnHover()) {
                    createTooltip(d, false);
                }
            },
            onClick: d => createTooltip(d, true),
            autoHighlight: true,
            highlightColor: [0, 0, 0, 48],
            visible: !layerDef.isHidden
        });
    }

    public createLayer = (
        layerDef: LayerDef,
        reactOnHover: () => boolean,
        setTooltip: (tooltip: JSX.Element, isPinned: boolean) => void
    ): Layer => {
        switch (layerDef.type) {
            case "point":
                return this.createPointLayer(layerDef, reactOnHover, setTooltip);
            case "cluster":
                return this.createClusterLayer(layerDef, reactOnHover, setTooltip);
            case "geojson":
                return this.createGeojsonLayer(layerDef, reactOnHover, setTooltip);
        }
    }

}